import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../../i18n/useLanguage';

// Nominatim API types
interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  locationName?: string;
  radiusKm?: number; // Radius in kilometers for visualization
  onLocationChange: (latitude?: number, longitude?: number, name?: string) => void;
  error?: string;
  disabled?: boolean;
}

// Component to handle map clicks
function MapClickHandler({ onLocationChange, disabled }: { onLocationChange: (lat: number, lon: number) => void; disabled?: boolean }) {
  useMapEvents({
    click: (e) => {
      if (!disabled) {
        const { lat, lng } = e.latlng;
        onLocationChange(lat, lng);
      }
    },
  });
  return null;
}

// Component to sync map view with external coordinates
function MapViewSync({ lat, lon }: { lat?: number; lon?: number }) {
  const map = useMap();
  
  useEffect(() => {
    if (lat !== undefined && lon !== undefined && !isNaN(lat) && !isNaN(lon)) {
      map.setView([lat, lon], map.getZoom(), { animate: true });
    }
  }, [lat, lon, map]);
  
  return null;
}

/**
 * Component for selecting or entering a location with interactive map.
 */
export const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  locationName,
  radiusKm,
  onLocationChange,
  error,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const VIENNA_COORDS: [number, number] = [48.2082, 16.3738];
  // Two notches further out than the previous default (13) so the map
  // starts with more surrounding context when it first appears.
  const DEFAULT_ZOOM = 11;

  const [name, setName] = useState(locationName || '');
  const [lat, setLat] = useState(latitude?.toString() || '');
  const [lon, setLon] = useState(longitude?.toString() || '');
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : VIENNA_COORDS
  );
  const [mapZoom, setMapZoom] = useState(latitude && longitude ? DEFAULT_ZOOM : DEFAULT_ZOOM);
  // Map starts collapsed so the picker takes a single line by default.
  // It auto-expands when the user interacts with the search row or when a
  // location is supplied via props (e.g. when editing an existing post).
  const [isMapExpanded, setIsMapExpanded] = useState<boolean>(
    latitude !== undefined && longitude !== undefined
  );
  
  // Location search state
  const [searchQuery, setSearchQuery] = useState(locationName || '');
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Try to get user's current location on mount if no location is provided
  useEffect(() => {
    // Only try geolocation if no location is provided via props
    if (latitude === undefined && longitude === undefined && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLon = position.coords.longitude;
          setMapCenter([userLat, userLon]);
          setMapZoom(DEFAULT_ZOOM);
        },
        () => {
          // If geolocation fails or is denied, use Vienna as default
          setMapCenter(VIENNA_COORDS);
          setMapZoom(DEFAULT_ZOOM);
        },
        {
          timeout: 5000,
          enableHighAccuracy: false,
        }
      );
    }
  }, []); // Only run on mount

  // Update map center when coordinates change via text input
  useEffect(() => {
    const parsedLat = parseFloat(lat);
    const parsedLon = parseFloat(lon);
    if (!isNaN(parsedLat) && !isNaN(parsedLon)) {
      setMapCenter([parsedLat, parsedLon]);
      setMapZoom(DEFAULT_ZOOM);
    }
  }, [lat, lon]);

  // Update text inputs when props change
  useEffect(() => {
    if (latitude !== undefined) setLat(latitude.toString());
    if (longitude !== undefined) setLon(longitude.toString());
    if (locationName !== undefined) {
      setName(locationName);
      setSearchQuery(locationName);
    }
    if (latitude !== undefined && longitude !== undefined) {
      setMapCenter([latitude, longitude]);
      setMapZoom(DEFAULT_ZOOM);
      setIsMapExpanded(true);
    }
  }, [latitude, longitude, locationName]);

  // Nominatim reverse — fills search field when pin is placed on map or via GPS
  const reverseGeocode = useCallback(async (clickLat: number, clickLon: number): Promise<string | undefined> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${clickLat}&lon=${clickLon}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'ITI-Bulletin-Board/1.0',
            'Accept-Language': typeof navigator !== 'undefined' ? navigator.language : 'en',
          },
        }
      );
      if (!response.ok) return undefined;
      const data = (await response.json()) as { display_name?: string };
      return typeof data.display_name === 'string' ? data.display_name : undefined;
    } catch (e) {
      console.warn('Reverse geocode failed:', e);
      return undefined;
    }
  }, []);

  const applyResolvedAddress = useCallback(
    (clickLat: number, clickLon: number, address: string | undefined) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      setSearchResults([]);
      setShowResults(false);
      if (address) {
        setName(address);
        setSearchQuery(address);
        onLocationChange(clickLat, clickLon, address);
      } else {
        setName('');
        setSearchQuery('');
        onLocationChange(clickLat, clickLon, undefined);
      }
    },
    [onLocationChange]
  );

  const handleMapClick = useCallback(
    async (clickLat: number, clickLon: number) => {
      setLat(clickLat.toString());
      setLon(clickLon.toString());
      setMapCenter([clickLat, clickLon]);
      setIsMapExpanded(true);
      const address = await reverseGeocode(clickLat, clickLon);
      applyResolvedAddress(clickLat, clickLon, address);
    },
    [reverseGeocode, applyResolvedAddress]
  );

  const handleUseCurrentLocation = () => {
    setIsMapExpanded(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLat = position.coords.latitude;
          const newLon = position.coords.longitude;
          setLat(newLat.toString());
          setLon(newLon.toString());
          setMapCenter([newLat, newLon]);
          setMapZoom(DEFAULT_ZOOM);
          void reverseGeocode(newLat, newLon).then((address) => {
            applyResolvedAddress(newLat, newLon, address);
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          // Fallback to Vienna if geolocation fails
          setMapCenter(VIENNA_COORDS);
          setMapZoom(DEFAULT_ZOOM);
          alert(t('location.geoError'));
        },
        {
          timeout: 5000,
          enableHighAccuracy: false,
        }
      );
    } else {
      // Fallback to Vienna if geolocation not supported
      setMapCenter(VIENNA_COORDS);
      setMapZoom(DEFAULT_ZOOM);
      alert(t('location.geoNotSupported'));
    }
  };

  // Search for locations using Nominatim API
  const searchLocation = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      // Rate limiting: Nominatim allows 1 request per second
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
        {
          headers: {
            'User-Agent': 'ITI-Bulletin-Board/1.0', // Required by Nominatim
          },
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const results: NominatimResult[] = await response.json();
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching location:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearLocation = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setLat('');
    setLon('');
    setName('');
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    onLocationChange(undefined, undefined, undefined);
  }, [onLocationChange]);

  // Debounced search handler
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setShowResults(true);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (value.trim() === '') {
        clearLocation();
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchLocation(value);
      }, 500);
    },
    [searchLocation, clearLocation]
  );

  // Handle search result selection
  const handleSelectResult = useCallback((result: NominatimResult) => {
    const resultLat = parseFloat(result.lat);
    const resultLon = parseFloat(result.lon);
    
    setLat(resultLat.toString());
    setLon(resultLon.toString());
    setMapCenter([resultLat, resultLon]);
    setMapZoom(DEFAULT_ZOOM);
    setName(result.display_name);
    // Reflect the picked location in the search field instead of clearing
    // it. Cancel any pending debounced search so the input doesn't refetch
    // results for the just-selected name.
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchQuery(result.display_name);
    setSearchResults([]);
    setShowResults(false);
    onLocationChange(resultLat, resultLon, result.display_name);
  }, [onLocationChange]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const hasValidCoordinates = !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lon));
  const markerPosition: [number, number] | null = hasValidCoordinates
    ? [parseFloat(lat), parseFloat(lon)]
    : null;

  const showClearActive =
    !disabled && (Boolean(searchQuery.trim()) || hasValidCoordinates);
  const showClearSlot = !disabled;

  return (
    <div className="form-group location-picker">
      {/* Location Search + current-location pin */}
      <div className="location-picker__search" ref={searchContainerRef}>
        <div
          className="location-picker__search-row"
          onClick={() => setIsMapExpanded(true)}
        >
          <input
            type="text"
            placeholder={t('location.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setIsMapExpanded(true)}
            className="location-picker__search-input"
            disabled={disabled}
          />
          {showClearSlot && (
            <button
              type="button"
              className={clsx(
                'location-picker__clear-button',
                !showClearActive && 'location-picker__clear-button--reserved'
              )}
              onClick={(e) => {
                e.stopPropagation();
                clearLocation();
              }}
              tabIndex={showClearActive ? 0 : -1}
              aria-hidden={!showClearActive}
              aria-label={t('post.clear')}
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleUseCurrentLocation();
            }}
            className="location-picker__current-button"
            disabled={disabled}
            aria-label={t('location.useCurrentLocation')}
            title={t('location.useCurrentLocation')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s-7-7.58-7-12a7 7 0 0 1 14 0c0 4.42-7 12-7 12z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
          </button>
        </div>
        {showResults && (searchResults.length > 0 || isSearching) && (
          <div className="location-picker__search-results">
            {isSearching && (
              <div className="location-picker__search-loading">{t('location.searching')}</div>
            )}
            {!isSearching && searchResults.map((result) => (
              <button
                key={result.place_id}
                type="button"
                className="location-picker__search-result"
                onClick={() => handleSelectResult(result)}
              >
                {result.display_name}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Map Display */}
      {isMapExpanded && (
      <div className="location-picker__map" style={{ height: '400px', width: '100%', marginBottom: '1rem' }}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          data-testid="map-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            data-testid="tile-layer"
          />
          <MapClickHandler onLocationChange={handleMapClick} disabled={disabled} />
          {!isNaN(parseFloat(lat)) && !isNaN(parseFloat(lon)) && (
            <MapViewSync lat={parseFloat(lat)} lon={parseFloat(lon)} />
          )}
          {markerPosition && <Marker position={markerPosition} data-testid="marker" />}
          {markerPosition && radiusKm && radiusKm > 0 && (
            <Circle
              center={markerPosition}
              radius={radiusKm * 1000} // Convert km to meters
              pathOptions={{
                color: '#3388ff',
                fillColor: '#3388ff',
                fillOpacity: 0.2,
                weight: 2,
              }}
              data-testid="radius-circle"
            />
          )}
        </MapContainer>
      </div>
      )}

      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

